import { useState } from "react";
import {
  deleteAttachment,
  getBlob,
  saveEDSSCase,
  writeAttachment,
} from "../api/api";
import {
  buildInitialWritePayload,
  createWritePayloadEntry,
  updateReadModel,
} from "../utils/attachment";

export function useAttachments() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const remove = async (
    caseId,
    personId,
    aid,
    existingAttachmentData,
    mutateEdssData
  ) => {
    setLoading(true);
    setError(null);

    try {
      const payload = buildInitialWritePayload(
        caseId,
        personId,
        existingAttachmentData
      );

      // Fix up the case first so we don't leave an invalid reference to a bad blob
      payload.ATTACHMENTS.ATTACHMENTS = payload.ATTACHMENTS.ATTACHMENTS.filter(
        (x) => x.AID !== aid
      );
      await saveEDSSCase(payload);

      // Only remove the actual artifact once the case has been written
      await deleteAttachment(caseId, aid);

      // Patch the UI
      mutateEdssData((draft) => {
        updateReadModel(draft, payload.ATTACHMENTS.ATTACHMENTS);
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const download = async (container, blob, filename) => {
    const data = await getBlob(container, blob);

    const url = window.URL.createObjectURL(data);

    const a = document.createElement("a");
    a.href = url;

    a.download = filename;

    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const edit = async (
    caseId,
    personId,
    existingAttachmentData,
    aid,
    filename,
    description,
    mutateEdssData
  ) => {
    setLoading(true);
    setError(null);

    const payload = buildInitialWritePayload(
      caseId,
      personId,
      existingAttachmentData
    );

    try {
      const forEdit = payload.ATTACHMENTS.ATTACHMENTS.find(
        (x) => x.AID === aid
      );
      forEdit.FILE_NAME = filename;
      forEdit.NAME = description;

      await saveEDSSCase(payload);

      mutateEdssData((draft) => {
        updateReadModel(draft, payload.ATTACHMENTS.ATTACHMENTS);
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  const upload = async (
    caseId,
    personId,
    existingAttachmentData,
    files,
    mutateEdssData
  ) => {
    setLoading(true);
    setError(null);

    const payload = buildInitialWritePayload(
      caseId,
      personId,
      existingAttachmentData
    );

    try {
      for (const file of files) {
        const aid = await writeAttachment(caseId, file);

        const today = new Date();
        const filename = file.name;
        const newEntry = createWritePayloadEntry(aid, filename, today);

        // Add the newly attached file to the case payload
        payload.ATTACHMENTS.ATTACHMENTS.push(newEntry);
      }

      // If every file was uploaded successfully, write the updated case
      await saveEDSSCase(payload);

      mutateEdssData((draft) => {
        updateReadModel(draft, payload.ATTACHMENTS.ATTACHMENTS);
      });
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { upload, download, edit, remove, loading, error };
}
